import { isGlobalFeatureEnabled } from 'owa-feature-flags';
import { AdminRoles, type AdminRbacQueryInput } from '../interfaces';
import { queryAdminRbacWithRetry } from '../services/rbacQuery.service';
import {
    endQueryingRbacMutator,
    incrementCounterMutator,
    markPermissionCounterMutator,
    removePermissionMutator,
    updatePendingRequestsMutator,
    failQueryingRbacMutator,
} from './mutators';
import { getStore } from './store';
import { canAccessManagementPage } from './selectors';

export async function checkAdminRbacAsync(
    inputs: AdminRbacQueryInput[],
    force?: boolean
): Promise<Partial<Record<AdminRbacQueryInput, boolean | null>>> {
    try {
        const toCheck = force
            ? inputs
            : inputs.filter(input => {
                  const permissionMap = getStore().permissionMap;
                  const pendingRequests = getStore().pendingRequests;
                  return !permissionMap.has(input) && !pendingRequests.has(input);
              });
        if (toCheck.length > 0) {
            incrementCounterMutator();
            const counter = getStore().counter;

            markPermissionCounterMutator(toCheck, counter);
            const promise = queryAdminRbacWithRetry(toCheck)
                .then(response => {
                    endQueryingRbacMutator(response, counter);
                })
                .catch(() => {
                    failQueryingRbacMutator(toCheck);
                });

            updatePendingRequestsMutator(toCheck, promise);

            await promise;
        }
        await Promise.all(
            inputs.map(async input => {
                await getStore().pendingRequests.get(input);
            })
        );
    } catch {
        removePermissionMutator(inputs);
    }
    const permissionMap = getStore().permissionMap;
    const result: Partial<Record<AdminRbacQueryInput, boolean | null>> = {};
    for (const input of inputs) {
        result[input] = permissionMap.get(input) ?? null; // If it's undefined, it means error happened for the input
    }
    return result;
}

/**
 *
 * @param force - If true, it will force the check even if the permission is already cached.
 * @returns boolean indicating if the user can access the management page.
 *          If null, it means an error occurred.
 */
export async function checkIfCanAccessManagementPage(force?: boolean): Promise<boolean | null> {
    const queryBuildingDeskAdmin = isGlobalFeatureEnabled(
        'msplaces-space-management-rbac-query-building-desk-admin'
    );
    const result = await checkAdminRbacAsync(
        [
            AdminRoles.tenantPlacesManagement,
            ...(queryBuildingDeskAdmin
                ? [AdminRoles.placesBuildingManagement, AdminRoles.PlacesDeskManagement]
                : []),
        ],
        force
    );
    // if all roles are null, it means an error occurred
    if (Object.values(result).every(value => value == null)) {
        if (queryBuildingDeskAdmin) {
            // If error occurred, we can fallback to checking only tenantPlacesManagement
            const fallbackResult = await checkAdminRbacAsync([AdminRoles.tenantPlacesManagement]);
            return fallbackResult[AdminRoles.tenantPlacesManagement] ?? null;
        }

        return null;
    }
    return canAccessManagementPage();
}
